/**
 * Auth Store
 * Zustand store for authentication state management.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@types/common.types';
import { STORAGE_KEYS } from '@config/constants';

/**
 * Authentication state interface.
 */
interface AuthState {
  /** Current authenticated user */
  user: User | null;
  /** Authentication token */
  token: string | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Loading state for auth operations */
  isLoading: boolean;
  /** Flag to track if user explicitly logged out */
  wasExplicitlyLoggedOut?: boolean;
  /** When set, admin is impersonating another user in read-only mode */
  impersonation?: {
    originalUser: User;
    originalToken: string;
    targetUser: User;
  } | null;
}

/**
 * Authentication actions interface.
 */
interface AuthActions {
  /** Set the authenticated user and token */
  setAuth: (user: User, token: string) => void;
  /** Update user data */
  setUser: (user: User) => void;
  /** Clear authentication state (logout) */
  clearAuth: () => void;
  /** Set loading state */
  setLoading: (isLoading: boolean) => void;
  /** Start impersonation: switches auth to target user (readonly token) */
  startImpersonation: (payload: { targetUser: User; targetToken: string }) => void;
  /** Stop impersonation: restores original admin auth */
  stopImpersonation: () => void;
}

/**
 * Combined auth store type.
 */
type AuthStore = AuthState & AuthActions;

/**
 * Initial state values.
 */
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  wasExplicitlyLoggedOut: false,
  impersonation: null,
};

/**
 * Auth store with persistence.
 * Uses localStorage to persist authentication state across sessions.
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      setAuth: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          wasExplicitlyLoggedOut: false, // Reset flag when logging in
          impersonation: null,
        }),

      setUser: (user) =>
        set({
          user,
        }),

      clearAuth: () =>
        set({
          ...initialState,
          wasExplicitlyLoggedOut: true, // Set flag when explicitly logging out
        }),

      setLoading: (isLoading) =>
        set({
          isLoading,
        }),

      startImpersonation: ({ targetUser, targetToken }) =>
        set((state) => {
          if (!state.user || !state.token) return state;
          return {
            user: targetUser,
            token: targetToken,
            isAuthenticated: true,
            isLoading: false,
            wasExplicitlyLoggedOut: false,
            impersonation: {
              originalUser: state.user,
              originalToken: state.token,
              targetUser,
            },
          };
        }),

      stopImpersonation: () =>
        set((state) => {
          if (!state.impersonation) return state;
          return {
            user: state.impersonation.originalUser,
            token: state.impersonation.originalToken,
            isAuthenticated: true,
            isLoading: false,
            wasExplicitlyLoggedOut: false,
            impersonation: null,
          };
        }),
    }),
    {
      name: STORAGE_KEYS.AUTH_TOKEN,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        impersonation: state.impersonation,
      }),
    }
  )
);

/**
 * Selector hooks for common auth state values.
 */
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);

